import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Car } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { ZipCodeInput } from "@/components/ZipCodeInput";
import { RadiusSelector } from "@/components/RadiusSelector";
import { useUserLocation } from "@/hooks/useUserLocation";
import SubscriptionCard from "@/components/SubscriptionCard";
import { ROUTES } from "@/lib/routes";
import { NotificationSettings } from "@/components/settings/NotificationSettings";

// Username validation schema
const usernameSchema = z.string()
  .min(3, "Username must be at least 3 characters long")
  .max(20, "Username must be no more than 20 characters long")  
  .regex(/^[A-Za-z0-9_-]+$/, "Username can only contain letters, numbers, hyphens, and underscores");

const Settings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Form state
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [profileVisibility, setProfileVisibility] = useState(true);
  const [usernameError, setUsernameError] = useState("");

  // Load user data on mount
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('username, profile_visibility')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading profile:', error);
        return;
      }

      if (data) {
        setUsername(data.username || "");
        setProfileVisibility(data.profile_visibility ?? true);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const validateUsername = (value: string) => {
    try {
      usernameSchema.parse(value);
      setUsernameError("");
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setUsernameError(error.issues[0].message);
      }
      return false;
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    if (value) {
      validateUsername(value);
    } else {
      setUsernameError("");
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    // Validate username before saving
    if (!validateUsername(username)) {
      return;
    }

    setLoading(true);
    try {
      // Update auth user email if changed
      if (email !== user.email) {
        const { error: authError } = await supabase.auth.updateUser({
          email: email
        });
        if (authError) throw authError;
      }

      // Update or insert user profile
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          email: email,
          username: username,
        });

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile information has been saved successfully.",
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Error",
        description: "Failed to save profile changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileVisibilityChange = async (checked: boolean) => {
    if (!user?.id) return;

    setProfileLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          profile_visibility: checked,
        });

      if (error) throw error;

      setProfileVisibility(checked);
      toast({
        title: "Privacy settings updated",
        description: "Your profile visibility has been updated.",
      });
    } catch (error) {
      console.error('Error updating privacy:', error);
      toast({
        title: "Error",
        description: "Failed to update privacy settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-3xl font-bold text-foreground mb-2">Settings</h1>
            <p className="text-muted-foreground mb-8">Please log in to access your settings.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <SettingsContent user={user} toast={toast} />
  );
};

const HomeLocationCard = () => {
  const { location, isLoading, updateLocation, isUpdating } = useUserLocation();
  const [localZip, setLocalZip] = useState(location?.home_zip_code || '');
  const [localCity, setLocalCity] = useState(location?.home_city || '');
  const [localState, setLocalState] = useState(location?.home_state || '');
  const [localLat, setLocalLat] = useState(location?.home_latitude || 0);
  const [localLon, setLocalLon] = useState(location?.home_longitude || 0);
  const [localRadius, setLocalRadius] = useState(location?.local_radius_miles || 200);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (location) {
      setLocalZip(location.home_zip_code || '');
      setLocalCity(location.home_city || '');
      setLocalState(location.home_state || '');
      setLocalLat(location.home_latitude || 0);
      setLocalLon(location.home_longitude || 0);
      setLocalRadius(location.local_radius_miles || 200);
    }
  }, [location]);

  const handleZipCodeChange = (zipCode: string, city: string, state: string, lat: number, lon: number) => {
    setLocalZip(zipCode);
    setLocalCity(city);
    setLocalState(state);
    setLocalLat(lat);
    setLocalLon(lon);
    setHasChanges(true);
  };

  const handleRadiusChange = (radius: number) => {
    setLocalRadius(radius);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateLocation({
      home_zip_code: localZip,
      home_city: localCity,
      home_state: localState,
      home_latitude: localLat,
      home_longitude: localLon,
      local_radius_miles: localRadius,
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Home Location</CardTitle>
          <CardDescription>Loading...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Home Location</CardTitle>
        <CardDescription>Set your home location to see local activity in your feed</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ZipCodeInput
          value={localZip}
          onChange={handleZipCodeChange}
          city={localCity}
          state={localState}
          disabled={isUpdating}
        />
        
        <RadiusSelector
          value={localRadius}
          onChange={handleRadiusChange}
          disabled={isUpdating}
        />

        <Button 
          onClick={handleSave} 
          disabled={isUpdating || !hasChanges || !localLat}
        >
          {isUpdating ? "Saving..." : "Save Location"}
        </Button>
      </CardContent>
    </Card>
  );
};

interface SettingsContentProps {
  user: { id: string; email?: string };
  toast: ReturnType<typeof useToast>['toast'];
}

const SettingsContent = ({ user, toast }: SettingsContentProps) => {
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Form state
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [profileVisibility, setProfileVisibility] = useState(true);
  const [usernameError, setUsernameError] = useState("");

  // Load user data on mount
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('username, profile_visibility')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading profile:', error);
        return;
      }

      if (data) {
        setUsername(data.username || "");
        setProfileVisibility(data.profile_visibility ?? true);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const validateUsername = (value: string) => {
    try {
      usernameSchema.parse(value);
      setUsernameError("");
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setUsernameError(error.issues[0].message);
      }
      return false;
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    if (value) {
      validateUsername(value);
    } else {
      setUsernameError("");
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    // Validate username before saving
    if (!validateUsername(username)) {
      return;
    }

    setLoading(true);
    try {
      // Update auth user email if changed
      if (email !== user.email) {
        const { error: authError } = await supabase.auth.updateUser({
          email: email
        });
        if (authError) throw authError;
      }

      // Update or insert user profile
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          email: email,
          username: username,
        });

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile information has been saved successfully.",
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Error",
        description: "Failed to save profile changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileVisibilityChange = async (checked: boolean) => {
    if (!user?.id) return;

    setProfileLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          profile_visibility: checked,
        });

      if (error) throw error;

      setProfileVisibility(checked);
      toast({
        title: "Privacy settings updated",
        description: "Your profile visibility has been updated.",
      });
    } catch (error) {
      console.error('Error updating privacy:', error);
      toast({
        title: "Error",
        description: "Failed to update privacy settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProfileLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-foreground mb-2">Settings</h1>
          <p className="text-muted-foreground mb-8">Manage your account preferences</p>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input 
                    id="username" 
                    placeholder="Enter your username (3-20 characters)"
                    value={username}
                    onChange={handleUsernameChange}
                    className={usernameError ? "border-destructive" : ""}
                  />
                  {usernameError && (
                    <p className="text-sm text-destructive">{usernameError}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Only letters, numbers, hyphens (-), and underscores (_) allowed. No spaces.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Button 
                  onClick={handleSaveProfile} 
                  disabled={loading || !!usernameError || !username.trim()}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Privacy Settings</CardTitle>
                <CardDescription>Control your data visibility</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Public Profile</Label>
                    <p className="text-sm text-muted-foreground">Allow others to view your profile</p>
                  </div>
                  <Switch 
                    checked={profileVisibility}
                    onCheckedChange={handleProfileVisibilityChange}
                    disabled={profileLoading}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-muted-foreground" />
                  <CardTitle>My Garage</CardTitle>
                </div>
                <CardDescription>
                  Manage your vehicles on your profile page
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Add, edit, and set your featured vehicle from your profile. Your featured vehicle appears in your avatar across the app.
                </p>
                <Button variant="outline" asChild>
                  <Link to={ROUTES.profile(user.id)}>
                    Go to My Profile
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <HomeLocationCard />

            <SubscriptionCard />

            <NotificationSettings />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;